import { v4 as uuidv4 } from "uuid";

import prepareFileUpload from "@components/wysiwyg/graphql/prepare-topic-file-upload-mutation";
import client from "@graphql/client";

export type AskAIAttachment = {
  id: string;
  filename: string;
  previewUrl: string;
  url?: string;
  isImage: boolean;
  isUploading: boolean;
};

export const maxAskAIAttachments = 4;
const imageMimeTypes = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
]);
const documentMimeTypes: Record<string, string> = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  txt: "text/plain",
  md: "text/markdown",
  csv: "text/csv",
};
export const attachmentAccept = [
  ...imageMimeTypes,
  ...Object.keys(documentMimeTypes).map((extension) => `.${extension}`),
].join(",");

export function getAttachmentFormat(file: File) {
  if (imageMimeTypes.has(file.type)) {
    return { isImage: true, contentType: file.type, maxBytes: 5 * 1024 * 1024 };
  }
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  const contentType = documentMimeTypes[extension];
  return contentType
    ? { isImage: false, contentType, maxBytes: 4 * 1024 * 1024 }
    : undefined;
}

export async function uploadAskAIFile(file: File, organizationId: number) {
  const format = getAttachmentFormat(file);
  if (!format) throw new Error("Unsupported file format.");
  const { data } = await client.mutate({
    mutation: prepareFileUpload,
    variables: {
      organizationId,
      filename: `ask-ai/${uuidv4()}/${file.name.replaceAll(/[\\/]/g, "_")}`,
      contentType: format.contentType,
    },
  });
  const { fileUploadUrl, fileViewingUrl } = data.prepareFileUpload;
  const response = await fetch(fileUploadUrl, {
    method: "PUT",
    headers: { "Content-Type": format.contentType },
    body: file,
  });
  if (!response.ok) throw new Error("The file could not be uploaded.");
  return fileViewingUrl as string;
}
